/**
 * BrainDumpGraph — interactive knowledge graph visualization.
 *
 * A13-N13. Sub-view within BrainDumpPage (tab navigation).
 * Uses reactflow for node/edge rendering with drag support.
 *
 * Props:
 *  - onNoteSelect: (noteId: string) => void
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { apiClient } from '../../lib/apiClient';

const API_PREFIX = '/api/v1/aif/brain-dump';

// Graph visualization colors — uses CSS vars with hex fallbacks for ReactFlow
// (ReactFlow style prop needs resolved colors; CSS vars work in inline style)
const NODE_COLORS = {
  note: 'var(--aif-color-warning-light)',
  concept: 'var(--aif-bg-tertiary)',
  entity_field: 'var(--aif-color-success-light)',
  entity_cow: 'var(--aif-color-warning-light)',
  entity_machine: 'var(--aif-color-info-light)',
  entity_building: 'var(--aif-color-warning-light)',
  entity_supplier: 'var(--aif-color-info-light)',
  entity_default: 'var(--aif-color-info-light)',
};

const ENTITY_ICONS = {
  note: '\uD83D\uDCDD',
  concept: '\uD83D\uDCA1',
  field: '\uD83C\uDF3E',
  cow: '\uD83D\uDC04',
  pig: '\uD83D\uDC16',
  machine: '\uD83D\uDE9C',
  building: '\uD83C\uDFE0',
  crop: '\uD83C\uDF31',
  supplier: '\uD83C\uDFE2',
};

function getNodeColor(node) {
  if (node.node_type === 'note') return NODE_COLORS.note;
  if (node.node_type === 'concept') return NODE_COLORS.concept;
  return NODE_COLORS[`entity_${node.entity_type}`] || NODE_COLORS.entity_default;
}

function getNodeIcon(node) {
  if (node.node_type === 'note') return ENTITY_ICONS.note;
  if (node.node_type === 'concept') return ENTITY_ICONS.concept;
  return ENTITY_ICONS[node.entity_type] || '\uD83D\uDD17';
}

/**
 * Layout nodes in concentric rings by type: notes center, concepts + entities outer.
 */
function layoutNodes(apiNodes) {
  const notes = apiNodes.filter((n) => n.node_type === 'note');
  const concepts = apiNodes.filter((n) => n.node_type === 'concept');
  const entities = apiNodes.filter((n) => n.node_type === 'entity');

  const result = [];
  const cx = 500;
  const cy = 400;

  notes.forEach((n, i) => {
    const angle = (2 * Math.PI * i) / Math.max(notes.length, 1);
    const r = Math.min(150, 40 + notes.length * 8);
    result.push({ ...n, position: { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) } });
  });

  concepts.forEach((n, i) => {
    const angle = Math.PI + (Math.PI * i) / Math.max(concepts.length, 1);
    const r = 320;
    result.push({ ...n, position: { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) } });
  });

  entities.forEach((n, i) => {
    const angle = (Math.PI * i) / Math.max(entities.length, 1);
    const r = 320;
    result.push({ ...n, position: { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) } });
  });

  return result;
}

export default function BrainDumpGraph({ onNoteSelect }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [stats, setStats] = useState({ notes: 0, concepts: 0, entities: 0 });

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const loadGraph = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(`${API_PREFIX}/graph?max_nodes=200`);
      const data = res.data ?? res;

      const positioned = layoutNodes(data.nodes || []);

      const flowNodes = positioned.map((n) => ({
        id: n.id,
        type: 'default',
        position: n.position,
        data: {
          label: `${getNodeIcon(n)} ${n.label}`,
          original: n,
        },
        style: {
          background: getNodeColor(n),
          border: '1px solid var(--aif-border-default)',
          borderRadius: 'var(--aif-radius-lg, 8px)',
          padding: '8px 10px',
          fontSize: '11px',
          maxWidth: 180,
          cursor: 'pointer',
        },
      }));

      const flowEdges = (data.edges || []).map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        label: e.link_type,
        type: 'smoothstep',
        style: { stroke: 'var(--aif-border-default)' },
        labelStyle: { fontSize: 9, fill: 'var(--aif-text-muted)' },
        animated: e.confidence != null && e.confidence < 0.7,
      }));

      setNodes(flowNodes);
      setEdges(flowEdges);
      setTruncated(data.truncated || false);
      setStats({
        notes: data.total_notes || 0,
        concepts: data.total_concepts || 0,
        entities: data.total_entities || 0,
      });
    } catch (err) {
      setError(err.message || 'Graph load failed');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadGraph();
  }, [loadGraph]);

  function handleNodeClick(_event, node) {
    setSelectedNode(node.data?.original || null);
  }

  function handleNodeDoubleClick(_event, node) {
    const original = node.data?.original;
    if (original?.node_type === 'note' && original?.data?.note_id) {
      onNoteSelect(original.data.note_id);
    }
  }

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 120px)' }} data-testid="graph-view">
      {/* Stats bar */}
      <div className="flex items-center gap-3 mb-2 text-xs text-[var(--aif-text-muted)]" data-testid="graph-stats">
        <span>{stats.notes} {t('brainDump.graphNotes', 'Notizen')}</span>
        <span>{stats.concepts} {t('brainDump.graphConcepts', 'Konzepte')}</span>
        <span>{stats.entities} {t('brainDump.graphEntities', 'Entit\u00E4ten')}</span>
        {truncated && (
          <span className="text-[var(--aif-color-warning)]" data-testid="truncated-warning">
            ({t('brainDump.graphTruncated', 'max. 200 Knoten')})
          </span>
        )}
      </div>

      {/* Graph canvas */}
      <div className="flex-1 rounded-lg border border-[var(--aif-border-default)] overflow-hidden relative" data-testid="graph-canvas">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-[var(--aif-bg-page)] z-10">
            <span className="text-[var(--aif-text-muted)] animate-pulse">{t('common.loading', 'Lade...')}</span>
          </div>
        )}
        {error && (
          <div className="absolute top-3 left-3 right-3 bg-[var(--aif-color-danger-light)] border border-[var(--aif-color-danger-border)] text-[var(--aif-color-danger)] px-3 py-2 rounded text-sm z-10">
            {error}
          </div>
        )}
        {!loading && !error && nodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-[var(--aif-text-muted)] text-sm" data-testid="graph-empty">
            {t('brainDump.graphEmpty', 'Noch keine Verkn\u00FCpfungen. Erfasse Notizen, um den Graph aufzubauen.')}
          </div>
        )}

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={handleNodeClick}
          onNodeDoubleClick={handleNodeDoubleClick}
          fitView
          attributionPosition="bottom-right"
          minZoom={0.2}
          maxZoom={2}
        >
          <Background variant="dots" gap={20} size={1} />
          <Controls />
          <MiniMap
            pannable
            zoomable
            nodeColor={(node) => {
              const original = node.data?.original;
              return original ? getNodeColor(original) : 'var(--aif-bg-tertiary)';
            }}
          />
        </ReactFlow>

        {/* Preview pane */}
        {selectedNode && (
          <div
            className="absolute top-3 right-3 w-64 bg-[var(--aif-bg-page)] border border-[var(--aif-border-default)] rounded-lg shadow-lg p-4 z-20"
            data-testid="node-preview"
          >
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-semibold text-sm text-[var(--aif-text-primary)]">
                {getNodeIcon(selectedNode)} {selectedNode.label}
              </h3>
              <button
                onClick={() => setSelectedNode(null)}
                className="text-[var(--aif-text-muted)] hover:text-[var(--aif-text-primary)]"
              >
                ×
              </button>
            </div>
            <div className="text-xs text-[var(--aif-text-secondary)] space-y-1">
              <div>Typ: <span className="font-medium">{selectedNode.node_type}</span></div>
              {selectedNode.entity_type && (
                <div>Entity: <span className="font-medium">{selectedNode.entity_type}</span></div>
              )}
              {selectedNode.data?.captured_at && (
                <div>
                  {t('brainDump.graphDate', 'Datum')}: {new Date(selectedNode.data.captured_at).toLocaleDateString('de-DE')}
                </div>
              )}
              {selectedNode.node_type === 'note' && selectedNode.data?.note_id && (
                <button
                  onClick={() => onNoteSelect(selectedNode.data.note_id)}
                  className="mt-2 text-[var(--aif-color-primary)] hover:underline text-xs"
                  data-testid="preview-open-note"
                >
                  {t('brainDump.openNote', 'Notiz öffnen')} →
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
